/**
 * Sites — the register's front door.
 *
 * A site, not a customer, is what jobs and assets hang off: buildings change
 * hands and the cameras stay where they are.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { Card, Chip, Empty, ErrorState, Loading, PageHeader } from "../components/ui";

export default function Sites() {
  const [search, setSearch] = useState("");
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["sites"],
    queryFn: () => api.sites.list(),
  });

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(
      (s) =>
        s.name.toLowerCase().includes(needle) ||
        s.customer_name.toLowerCase().includes(needle) ||
        (s.suburb ?? "").toLowerCase().includes(needle),
    );
  }, [data, search]);

  if (isPending) return <Loading what="Loading sites" />;
  if (error)
    return (
      <ErrorState
        error={error}
        onRetry={() => void refetch()}
        needs="The full site list is more than one device carries."
      />
    );

  return (
    <div className="page">
      <PageHeader title="Sites & assets">
        Register the hardware and the history follows it — warranty, firmware, test
        dates and repeat faults all fall out of that.
      </PageHeader>

      <div className="toolbar">
        <label className="search">
          <input
            type="search"
            value={search}
            placeholder="Site, customer or suburb"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {rows.length === 0 ? (
        <Card>
          <Empty title={data.length === 0 ? "No sites yet" : "Nothing matches"}>
            {data.length === 0
              ? "Sites arrive with customers. Seed the demo data to see the register working."
              : "Try a different search."}
          </Empty>
        </Card>
      ) : (
        <div className="site-grid">
          {rows.map((site) => (
            <Link key={site.id} to={`/sites/${site.id}`} className="site-card">
              <div className="site-card-head">
                <b>{site.name}</b>
                {site.contract_code && <Chip tone="ok">{site.contract_code}</Chip>}
              </div>
              <span className="muted tiny">{site.customer_name}</span>
              <span className="muted tiny">
                {[site.address_line, site.suburb, site.state].filter(Boolean).join(", ") || "No address"}
              </span>
              <div className="site-card-foot">
                <span>
                  <b className="mono">{site.asset_count}</b> assets
                </span>
                <span>
                  <b className="mono">{site.open_job_count}</b> open jobs
                </span>
                {site.induction_required && <Chip tone="warn">induction</Chip>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
